<template>
  <div
    class="form-group"
    :class="{
      'has-error': errors && errors.length > 0,
      'cursor-disabled': isDisabled
    }"
  >
    <label
      v-if="label"
      class="register-label"
    >
      {{ label }}<sup v-if="required">*</sup>
    </label>
    <VueMultiselect
      v-model="value"
      ref="multiselect"
      class="cursor-pointer"
      label="label"
      track-by="value"
      :class="{
        'toggle': isOpen,
        'selected': value,
        'open-on-top': openDirection === 'top'
      }"
      :options="options"
      :searchable="false"
      :placeholder="placeholder"
      :show-labels="false"
      :disabled="isDisabled"
      :openDirection="openDirection"
      :allow-empty="false"
      @beforeOpen="handleBeforeOpen"
      @open="isOpen = true"
      @close="isOpen = false"
      @select="(option) => $emit('select', option.value)"
    >
      <template slot="caret">
        <div class="multiselect__select">
          <img
            v-if="!value" 
            class="arrow-icon" 
            src="/images/icons/dropdown-arrow.svg" 
            alt=""
          >
          <img
            v-else            
            class="close-icon"
            src="/images/side-menu/close.png" 
            alt=""
            @mousedown="clearSelection"
          >
        </div>
      </template>
    </VueMultiselect>
    <span
      class="help-block"
      v-if="errors && errors.length > 0"
    >
      <span
        v-for="error in errors"
        :key="error"
      >
        {{ error }}
      </span>
    </span>
  </div>
</template>

<script>
import VueMultiselect from 'vue-multiselect'

export default {
  name: 'Dropdown',
  components: { VueMultiselect },

  props: {
    label: String,
    placeholder: String,
    name: String,
    errors: Array,
    required: {
      type: Boolean,
      default: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    options: Array,
    defaultValue: Object,
    openDirection: {
      type: String,
      default: 'bottom'
    }
  },

  watch: {
    defaultValue(newDefaultValue) {
      this.value = newDefaultValue;
    }
  },

  data () {
    return {
      isOpen: false,
      value: this.defaultValue || ''
    }
  },

  methods: {
    close() {
      this.$refs.multiselect.toggle(false);
      this.value = '';
    },

    handleBeforeOpen() {
      if (this.$refs.multiselect.$refs.search.contains(event.target)) {
        return true
      }
      event.preventDefault();
      this.close();
      return false;
    },

    clearSelection(event) {
      event.preventDefault();
      event.stopPropagation();
      this.value = '';
      this.$refs.multiselect.deactivate();
      this.$emit('clear')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../sass/_variables.scss';

::v-deep .multiselect--disabled {
  background: none;
  cursor: not-allowed;

  .multiselect__select {
    background: none;
  }
}

.form-control {
  height: 40px;
  border-radius: 8px;
  color: #2E3C54;

  height: 60px;
  padding-left: 15px;
  padding-right: 15px;
  background-color: #FFF;
  font-size: 17px;
  
  @media (min-width: $breakpoint-lg) {
    font-size: 20px;
  }

  @media (min-width: $breakpoint-xxl) {
    height: 117px;
    font-size: 40px;
    padding-left: 40px;
    padding-right: 40px;
  }
}

.has-error {
  ::v-deep .multiselect__tags {
    border: 2px solid red;
  }

  .toggle {
    ::v-deep .multiselect__tags {
      border-bottom: 0;
    }
  }

  ::v-deep .multiselect__content-wrapper {
    border: 2px solid red;
    border-top: 0;
  }
}

.form-group {
  .multiselect__select::before {
    content: unset;
  }

  ::v-deep .multiselect__tags {
    @extend .form-control;

    height: 45px;
    display: flex;
    align-items: center;
    border-radius: 8px;

    @media (min-width: $breakpoint-xxl) {  
      height: 112px;
      border-radius: 20px;
    }
  }
    
  .multiselect--active {
    ::v-deep .multiselect__select {
      top: -2px;
    }
  }

  ::v-deep .multiselect__placeholder {
    color: #8696B1;
  }

  ::v-deep .multiselect__select {
    z-index: 1;
    height: 100%;
    display: flex;
    align-items: center;
    right: 10px;

    @media (min-width: $breakpoint-xxl) {
      right: 55px;
    }
  }

  ::v-deep .multiselect__single {
    color: #2E3C54;
    font-size: 17px;
    @media (min-width: $breakpoint-lg) {
      font-size: 20px;
    }

    @media (min-width: $breakpoint-xxl) {
      font-size: 40px;
      line-height: 40px;
    }
  }

  ::v-deep .multiselect__option--highlight:after {
    content: unset;
  }

  ::v-deep .multiselect__element:has(.multiselect__option--highlight) {
    background: $bg-gray;
  }

  ::v-deep .multiselect__element:has(.multiselect__option--selected), ::v-deep .multiselect__option--selected {
    background: $bg-gray;
  }

  ::v-deep .multiselect__option--highlight {
    background: $bg-gray;
  }

  ::v-deep .multiselect__option {
    color: #2E3C54;
    padding-left: 15px;
    padding-right: 15px;
    font-weight: 400;
    height: 60px;
    display: flex;
    align-items: center;
    font-size: 17px;
    @media (min-width: $breakpoint-lg) {
      font-size: 20px;
    }

    @media (min-width: $breakpoint-xxl) {
      height: 117px;
      font-size: 40px;
      padding-left: 50px;
      padding-right: 50px;
    }
  }

  ::v-deep .multiselect__content {
    -webkit-padding-start: 0;
  }

  ::v-deep .multiselect__content-wrapper {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    @media (min-width: $breakpoint-xxl) {
      max-height: 500px !important;
      border-bottom-left-radius: 20px;
      border-bottom-right-radius: 20px;
    }
  }

  ::v-deep .multiselect__element {
    margin-bottom: 0;
  }

  ::v-deep .multiselect__element:not(:last-of-type) {
    border-bottom: 1px solid #D8DFEB;
  }
}


.arrow-icon {
  width: 20px;
  height: 30px;
  @media (min-width: $breakpoint-xxl) {
    width: 40px;
    height: 50px;
  }
}

.close-icon {
  width: 20px;
  @media (min-width: $breakpoint-xxl) {
    width: 40px;
  }
}

.toggle:not(.open-on-top) {
  ::v-deep .multiselect__tags {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.toggle.open-on-top {
  ::v-deep .multiselect__tags {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
}

.help-block {
  padding-left: 0;

  @media (min-width: $breakpoint-xxl) {
    font-size: 30px;
  }
}

.dropdown-filter {
  ::v-deep .multiselect__tags {
    border: 4px solid #2E3C54;
    height: 65px;
    @media (min-width: $breakpoint-xxl) {
      border-width: 9px;
      height: 119px;
    }
  }

  ::v-deep .multiselect__single {
    @media (min-width: $breakpoint-xxl) {
      font-size: 37px;
      padding-right: 40px;
    }
  }
  
  ::v-deep .multiselect--active {
    .multiselect__tags {
      border-bottom: 0;
    }

    .multiselect__content-wrapper {
      border: 4px solid #2E3C54;
      border-top: 0;
      @media (min-width: $breakpoint-xxl) {
        border-width: 9px;
      }
    }
  }

  ::v-deep .multiselect--disabled {
    border-color: #8696B1;
  }

  ::v-deep .multiselect__placeholder {
    color: #2E3C54;
    @media (min-width: $breakpoint-xxl) {
      line-height: 40px;
    }
  }

  ::v-deep span {
    font-weight: 500;
  }

  .selected {
    ::v-deep .multiselect__tags {
      background-color: $dark-bg;
    }

    ::v-deep .multiselect__single {
      padding-right: 22px;
      background-color: $dark-bg;
      color: #FFF;
    }
  }
}

.form-group {
  .open-on-top {
    ::v-deep .multiselect__content-wrapper {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      border-top: 4px solid #2E3C54;
      border-bottom: 0;

      @media (min-width: $breakpoint-xxl) {
        border-top-width: 8px;
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
      }
    }
  }
}

.registration {
  .open-on-top {
    ::v-deep .multiselect__content-wrapper {
      border-top: 0
    }
  }
}

.dropdown-filter {
  .open-on-top.toggle {
    ::v-deep.multiselect__tags {
        border-bottom: 4px solid #2E3C54;
        border-top: 0;
        border-top-left-radius: 0;
        border-top-right-radius: 0
      }
  }
}
</style>
  